import {
    getAnimationDurationFromElement,
    createElement,
    createCSSClass,
} from "../../../core/js/utilities";
import {
    actions,
    reducers,
    createStore,
    dispatchEvent,
    renderState,
} from "../../../core/js/state-machine";
import {
    componentClasses,
    componentName,
    defaultProps,
    eventMap,
    icons,
    svgs,
    schema,
    views,
} from "../../../core/components/message-bar/message-bar";
import validateInitialization from "../../../core/js/helpers/validate-initialization";
import { prefix } from "../../../core/design-tokens/meta";

/**
 * Initialize the Message Bar component
 * @param {HTMLElement} element Message Bar element
 * @param {defaultProps} props optional settings
 * @returns {{
 *  element: HTMLElement,
 *  showMessageBar: Function,
 *  closeMessageBar: Function,
 *  dispose: Function
 * }} public api object
 */
const MessageBar = (element, props = {}) => {
    const [success, mergedProps] = validateInitialization({
        componentName,
        defaultProps,
        element,
        props,
        views,
        schema,
    });
    if (!success) return;

    let closeButton;
    let dismissTimer;
    let timedMeter;
    // Used for syncing the component store with the initial state.
    let initShow = true;

    /**
     * Some of the methods need deprecation warning. To do this we run them through a map of error
     * messages depending on the method being deprecated.
     * @param {string} method
     */
    const deprecatedApiMethod = (method) => (next) => {
        const warningMap = {
            showMessageBar: 'This method has been deprecated in favor of using "show()"',
            closeMessageBar: 'This method has been deprecated in favor of using "hide()"',
        };
        console.warn(warningMap[method]);
        next();
    };

    const handleShow = () => {
        if (showStore.getState()) {
            // We are already showing.
            return;
        }
        showMessageBar();
    };

    const handleHide = () => {
        if (!showStore.getState()) {
            // We are already closed.
            return;
        }
        hideMessageBar();
    };

    /**
     * Closes the message bar and dispatches hide event
     * @returns
     */
    const hideMessageBar = () => {
        showStore.dispatch(actions.hide());
        dispatchEvent(eventMap[actions.hide().type], element);
    };

    /**
     * Reveals the message bar and dispatches show event
     * @public
     * @returns
     */
    const showMessageBar = () => {
        showStore.dispatch(actions.show());
        dispatchEvent(eventMap[actions.show().type], element);
    };

    const dispose = () => {
        if (closeButton) {
            closeButton.removeEventListener("click", hideMessageBar);
        }
        element[componentName] = null;
        element = null;
    };

    /**
     * When our component recieves new state - we need to render our view properly.
     * @param {boolean} isVisible
     */
    const handleStateChange = (isVisible) => {
        let timer = clearTimeout(timer);
        // Start the animation transitions
        renderState(isVisible ? views.showing : views.hiding, element);

        // @deprecated - in favor of using showing/hiding
        dispatchEvent(isVisible ? eventMap.opening : eventMap.closing, element);

        // Callback to update props when animation is completed
        const finishAnimation = () => {
            renderState(isVisible ? views.visible : views.hidden, element);
            dispatchEvent(isVisible ? eventMap.shown : eventMap.hidden, element);

            // @deprecated - in favor of using shown/hidden
            dispatchEvent(isVisible ? eventMap.visible : eventMap.closed, element);

            const nextTimedMessage = getFirstTimedMessage();

            if (
                nextTimedMessage &&
                nextTimedMessage !== element &&
                nextTimedMessage[componentName]
            ) {
                nextTimedMessage[componentName].startTimer();
            }
        };
        // Captures and convert the animation transition-duration of fade keyframes
        // in case of unexpected results use a 100ms fallback
        const duration = getAnimationDurationFromElement(element) - 50 || 100;
        timer = setTimeout(finishAnimation, duration);
    };

    /**
     * Return the first timed messagebar
     * Because we do not have a container/manager to maintain this, we need to do a query to find
     * the other siblings who also have timers.
     */
    const getFirstTimedMessage = () => {
        const messageTimerQueue = element.parentElement.querySelectorAll(
            `.${componentClasses.hasTimer}:not([aria-hidden=true])`
        );
        return messageTimerQueue[0];
    };

    /**
     * Determine whether or not this MessageBar is the first message bar in the queue;
     */
    const isFirstTimedMessage = () => {
        return getFirstTimedMessage() === element;
    };

    const startTimerAnimation = () => {
        const currentTimedMeter = timedMeter;
        const timerDuration = mergedProps.timerDuration;
        const timerDynamicClass = `${prefix}dynamic-message-bar-t-${timerDuration}`;

        if (
            createCSSClass(
                `.${componentClasses.hasTimer}  .${prefix}message-bar__timer > .${prefix}message-bar__timer-circle.${timerDynamicClass}`,
                {
                    animation: `${prefix}message-bar-timer-animation ${mergedProps.timerDuration}s linear`,
                }
            )
        ) {
            renderState({ classList: [timerDynamicClass] }, currentTimedMeter);

            // Class prevents animation/timer from being started by dismiss:hover if it is not the active timed message bar
            renderState({ classList: [componentClasses.activeTimer] }, element);
        }
    };

    const endTimerAnimation = () => {
        renderState({ classList: [`!${componentClasses.activeTimer}`] }, element);
        hideMessageBar();
    };

    // Init

    // Determine whether or not we are starting the component from an show or hide state.
    // Default is to have the item showing, but for Form, it starts as hidden
    if (
        element.classList.contains(componentClasses.messageBar.hide) ||
        // @deprecated - handle the case where in 2.10.0 we provided an example using the helper
        // class for display none. Future forward we will look to remove this checking for this
        // helper class and only support the dedicated class
        element.classList.contains("dds__d-none")
    ) {
        renderState(views.hidden, element);
        initShow = false;
    }

    const showStore = createStore(reducers.boolReducer, initShow);
    showStore.subscribe(handleStateChange);

    // TODO - fix up this logic where it's better (not during the release)
    if (mergedProps.dismissible === true) {
        closeButton = createElement("button", {
            class: componentClasses.closeButton,
            html: icons.close(),
            type: "button",
            "aria-label": mergedProps.srCloseLabel,
        });
        closeButton.addEventListener("click", hideMessageBar, false);
        element.appendChild(closeButton);
    }

    if (mergedProps.dismissible && mergedProps.timer) {
        // If timer is passed as prop, add the necessary class for timer
        if (!element.classList.contains(`${componentClasses.hasTimer}`)) {
            element.classList.add(`${componentClasses.hasTimer}`);
        }

        closeButton.classList.add(`${componentClasses.timerButton}`);
        closeButton.insertAdjacentHTML("afterbegin", svgs.dismissTimer);
        timedMeter = closeButton.querySelector("circle");
        dismissTimer = closeButton.querySelector(`.${prefix}message-bar__timer`);
        closeButton.appendChild(dismissTimer);
        element.appendChild(closeButton);
        closeButton.addEventListener("animationend", endTimerAnimation);

        if (isFirstTimedMessage()) startTimerAnimation(timedMeter, mergedProps.timerDuration);
    }

    const api = {
        element,
        startTimer: startTimerAnimation,
        showMessageBar: () => deprecatedApiMethod("showMessageBar")(handleShow),
        closeMessageBar: () => deprecatedApiMethod("closeMessageBar")(handleHide),
        show: handleShow,
        hide: handleHide,
        dispose,
    };

    element[componentName] = api;
    // Return ourselves incase we are captured in js.
    return api;
};

export default MessageBar;
